import React, { useMemo, useState } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { format, differenceInDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import SortableTable from '../SortableTable';
import { isCountableVisit } from '../../constants/visitsClassification';
import { UNIVERS_ALL, visitsWithDuration } from '../../constants/visitsUnivers';

// Enregistrement des composants Chart.js
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title
);

function VisitsStoresAnalysis({ visits, dateRange, selectedUnivers = UNIVERS_ALL }) {

  const [sortBy, setSortBy] = useState('visits'); // 'visits', 'frequency', 'duration'
  const [filterShopSign, setFilterShopSign] = useState('');
  const [showCount, setShowCount] = useState(10);

  // Analyser les données des magasins
  const storesData = useMemo(() => {
    // Seules les vraies visites magasin (hors supprimées / spéciales / non effectuées) comptent.
    const countableVisits = visits.filter(isCountableVisit);

    // Numerator and denominator from one population — see visitsWithDuration().
    const durationByStore = new Map();
    visitsWithDuration(countableVisits, selectedUnivers).forEach((v) => {
      if (!v.date) return;
      const acc = durationByStore.get(v.interventionPoint) || { total: 0, count: 0 };
      acc.total += v.durationMinutes;
      acc.count += 1;
      durationByStore.set(v.interventionPoint, acc);
    });
    // Extraire les dates min et max pour calculer la période totale
    let minDate = new Date();
    let maxDate = new Date(0);

    countableVisits.forEach(visit => {
      if (visit.date && visit.date < minDate) minDate = new Date(visit.date);
      if (visit.date && visit.date > maxDate) maxDate = new Date(visit.date);
      if (visit.plannedStart && visit.plannedStart < minDate) minDate = new Date(visit.plannedStart);
      if (visit.plannedStart && visit.plannedStart > maxDate) maxDate = new Date(visit.plannedStart);
    });

    // Créer un Map pour stocker les données par point de vente
    const storesMap = new Map();

    countableVisits.forEach(visit => {
      const storeId = visit.interventionPoint;
      const shopSign = visit.shopSign || 'Inconnu';

      if (!storesMap.has(storeId)) {
        storesMap.set(storeId, {
          storeId,
          shopSign,
          totalVisits: 0,
          completedVisits: 0,
          firstVisitDate: null,
          lastVisitDate: null,
          visitDates: new Set()
        });
      }

      const storeData = storesMap.get(storeId);

      // Incrémenter le nombre total de visites
      storeData.totalVisits++;

      // Vérifier si la visite a été complétée
      if (visit.date) {
        storeData.completedVisits++;
        storeData.visitDates.add(format(visit.date, 'yyyy-MM-dd'));

        // Mettre à jour la date de première visite
        if (!storeData.firstVisitDate || visit.date < storeData.firstVisitDate) {
          storeData.firstVisitDate = new Date(visit.date);
        }

        // Mettre à jour la date de dernière visite
        if (!storeData.lastVisitDate || visit.date > storeData.lastVisitDate) {
          storeData.lastVisitDate = new Date(visit.date);
        }

      }
    });

    // Convertir le Map en tableau et calculer les métriques supplémentaires
    const storesArray = Array.from(storesMap.values()).map(store => {
      // Averaged over the visits that actually carry a duration, not over every
      // completed row: the two differ, and the second silently pulls the mean down.
      const measured = durationByStore.get(store.storeId) || { total: 0, count: 0 };
      store.totalDuration = measured.total;
      const averageDuration = measured.count > 0
        ? Math.round(measured.total / measured.count)
        : 0;
      store.measuredVisits = measured.count;

      // Calculer la fréquence des visites (nombre de jours entre les visites en moyenne)
      let visitFrequency = 0;
      if (store.completedVisits > 1 && store.firstVisitDate && store.lastVisitDate) {
        const daysBetweenFirstAndLast = Math.max(1, differenceInDays(store.lastVisitDate, store.firstVisitDate));
        visitFrequency = Math.round(daysBetweenFirstAndLast / (store.completedVisits - 1));
      }

      // Correction du calcul de la projection annuelle
      let annualVisitsProjection = null; // Par défaut: indéfini

      // Ne calculer que si nous avons au moins 2 visites complétées et une fréquence valide
      if (store.completedVisits >= 2 && visitFrequency > 0) {
        // Utiliser directement la fréquence des visites déjà calculée
        // Si visitFrequency = nombre de jours entre visites
        // alors annualVisitsProjection = nombre de visites par an
        annualVisitsProjection = Math.round(365 / visitFrequency);
      }

      return {
        ...store,
        averageDuration,
        visitFrequency,
        annualVisitsProjection, // Peut être null
        uniqueVisitDays: store.visitDates.size
      };
    });

    return storesArray;
  }, [visits, selectedUnivers]);

  // Filtrer les données pour le graphique
  const filteredStores = useMemo(() => {
    let filtered = storesData;

    // Appliquer le filtre par enseigne ou point d'intervention
    if (filterShopSign) {
      const searchTerm = filterShopSign.toLowerCase();
      filtered = filtered.filter(store =>
        // Chercher dans l'enseigne
        (store.shopSign && store.shopSign.toLowerCase().includes(searchTerm)) ||
        // OU chercher dans l'identifiant du point d'intervention
        (store.storeId && store.storeId.toString().toLowerCase().includes(searchTerm))
      );
    }

    // Trier selon le critère sélectionné pour le graphique
    switch (sortBy) {
      case 'visits':
        filtered.sort((a, b) => b.totalVisits - a.totalVisits);
        break;
      case 'frequency':
        filtered.sort((a, b) => a.visitFrequency - b.visitFrequency);
        break;
      case 'duration':
        filtered.sort((a, b) => b.averageDuration - a.averageDuration);
        break;
      case 'annualVisits':
        filtered.sort((a, b) => b.annualVisitsProjection - a.annualVisitsProjection);
        break;
      default:
        filtered.sort((a, b) => b.totalVisits - a.totalVisits);
    }

    // Limiter le nombre d'éléments affichés pour le graphique
    return filtered.slice(0, showCount);
  }, [storesData, sortBy, filterShopSign, showCount]);

  // Définir les colonnes pour le SortableTable - avec correction du problème d'alignement
  const columns = useMemo(() => [
    {
      key: 'name', // Cette colonne semble fusionner storeId et shopSign
      label: 'Point de vente',
      filterable: true,
      render: (item) => item.storeId,
    },
    {
      key: 'shopSign', // Utiliser pour le filtrage, mais ne pas afficher
      label: 'Enseigne',
      filterable: true,
      render: (item) => item.shopSign, // Ne pas rendre cette colonne
      hidden: false // Cacher cette colonne
    },
    {
      key: 'visitsRatio',
      label: 'Visites',
      render: (item) => parseInt(item.completedVisits, 10),
      sortFunction: (a, b, direction) => {
        return direction === 'ascending'
          ? a.completedVisits - b.completedVisits
          : b.completedVisits - a.completedVisits;
      }
    },
    {
      key: 'averageDuration',
      label: 'Durée Moyenne',
      render: (item, isRawValue) => {
        if (isRawValue) return item.averageDuration;
        return formatDuration(item.averageDuration);
      }
    },
    {
      key: 'visitFrequency',
      label: 'Fréquence',
      render: (item, isRawValue) => {
        if (isRawValue) return item.visitFrequency;
        return item.visitFrequency > 0
          ? `${item.visitFrequency} jour${item.visitFrequency > 1 ? 's' : ''}`
          : 'N/A';
      },
      sortFunction: (a, b, direction) => {
        // Gestion spéciale pour les valeurs à 0 (N/A)
        if (a.visitFrequency === 0 && b.visitFrequency === 0) return 0;
        if (a.visitFrequency === 0) return direction === 'ascending' ? 1 : -1;
        if (b.visitFrequency === 0) return direction === 'ascending' ? -1 : 1;
        return direction === 'ascending'
          ? a.visitFrequency - b.visitFrequency
          : b.visitFrequency - a.visitFrequency;
      }
    },
    {
      key: 'annualVisitsProjection',
      label: 'Passages/an',
      render: (item, isRawValue) => {
        if (isRawValue) return item.annualVisitsProjection;
        return item.annualVisitsProjection !== null
          ? `${item.annualVisitsProjection} visites`
          : 'N/A';
      },
      sortFunction: (a, b, direction) => {
        // Gérer les valeurs null lors du tri
        if (a.annualVisitsProjection === null && b.annualVisitsProjection === null) return 0;
        if (a.annualVisitsProjection === null) return direction === 'ascending' ? -1 : 1;
        if (b.annualVisitsProjection === null) return direction === 'ascending' ? 1 : -1;

        return direction === 'ascending'
          ? a.annualVisitsProjection - b.annualVisitsProjection
          : b.annualVisitsProjection - a.annualVisitsProjection;
      }
    },
    {
      key: 'lastVisitDate',
      label: 'Dernière Visite',
      render: (item, isRawValue) => {
        if (isRawValue) {
          return item.lastVisitDate ? format(item.lastVisitDate, 'yyyy-MM-dd') : '';
        }
        return item.lastVisitDate
          ? format(item.lastVisitDate, 'dd/MM/yyyy', { locale: fr })
          : 'Jamais';
      },
      sortFunction: (a, b, direction) => {
        if (!a.lastVisitDate && !b.lastVisitDate) return 0;
        if (!a.lastVisitDate) return direction === 'ascending' ? -1 : 1;
        if (!b.lastVisitDate) return direction === 'ascending' ? 1 : -1;
        return direction === 'ascending'
          ? a.lastVisitDate.getTime() - b.lastVisitDate.getTime()
          : b.lastVisitDate.getTime() - a.lastVisitDate.getTime();
      }
    },
  ], []);

  // Données pour le graphique
  const chartData = useMemo(() => {
    // Utiliser une variable locale, pas une constante
    let labels = filteredStores.map(store =>
      `${store.storeId} (${store.shopSign})`
    );

    let datasets = [];

    // Différents ensembles de données selon le critère de tri
    if (sortBy === 'visits') {
      datasets = [
        {
          label: 'Visites planifiées',
          data: filteredStores.map(store => store.totalVisits),
          backgroundColor: '#3498db',
        },
        {
          label: 'Visites réalisées',
          data: filteredStores.map(store => store.completedVisits),
          backgroundColor: '#2ecc71',
        }
      ];
    } else if (sortBy === 'frequency') {
      datasets = [
        {
          label: 'Jours entre visites (moyenne)',
          data: filteredStores.map(store => store.visitFrequency),
          backgroundColor: '#f1c40f',
        }
      ];
    } else if (sortBy === 'duration') {
      datasets = [
        {
          label: 'Durée moyenne (minutes)',
          data: filteredStores.map(store => store.averageDuration),
          backgroundColor: '#9b59b6',
        }
      ];
    } else if (sortBy === 'annualVisits') {
      // Filtrer les magasins qui n'ont pas de projection définie
      const storesWithProjection = filteredStores.filter(store => store.annualVisitsProjection !== null);

      datasets = [
        {
          label: 'Passages projetés par an',
          data: storesWithProjection.map(store => store.annualVisitsProjection),
          backgroundColor: '#e74c3c',
        }
      ];

      // Mettre à jour les labels pour qu'ils correspondent aux données filtrées
      // Utiliser réassignation au lieu de redéclaration
      labels = storesWithProjection.map(store => `${store.storeId} (${store.shopSign})`);
    }

    return { labels, datasets };
  }, [filteredStores, sortBy]);

  // Options pour le graphique
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: sortBy === 'visits' ? 'Nombre de visites' :
                sortBy === 'annualVisits' ? 'Passages annuels' :
                sortBy === 'duration' ? 'Durée (minutes)' :
                'Passages annuels',
          color: document.documentElement.classList.contains('dark') ? 'rgb(229, 231, 235)' : 'rgb(55, 65, 81)',
        },
        ticks: {
          color: document.documentElement.classList.contains('dark') ? 'rgb(229, 231, 235)' : 'rgb(55, 65, 81)',
        },
        grid: {
          color: document.documentElement.classList.contains('dark') ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        }
      },
      x: {
        ticks: {
          color: document.documentElement.classList.contains('dark') ? 'rgb(229, 231, 235)' : 'rgb(55, 65, 81)',
          maxRotation: 45,
          minRotation: 45
        },
        grid: {
          color: document.documentElement.classList.contains('dark') ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        }
      }
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: document.documentElement.classList.contains('dark') ? 'rgb(229, 231, 235)' : 'rgb(55, 65, 81)',
        }
      },
      title: {
        display: true,
        text: sortBy === 'visits' ? 'Nombre de visites par point de vente' :
              sortBy === 'annualVisits' ? 'Passages annuels projetés par point de vente' :
              sortBy === 'duration' ? 'Durée moyenne des visites par point de vente' :
              'Passages annuels projetés par point de vente',
        color: document.documentElement.classList.contains('dark') ? 'rgb(229, 231, 235)' : 'rgb(55, 65, 81)',
      }
    }
  };

  // Formatage durée en heures:minutes ou jours selon la valeur
  const formatDuration = (minutes) => {
    if (!minutes) return "N/A";

    // Si la durée dépasse 72 heures (4320 minutes), afficher en jours
    if (minutes >= 4320) {
      const days = Math.floor(minutes / (60 * 24)); // Convertir en jours
      const remainingHours = Math.floor((minutes % (60 * 24)) / 60); // Heures restantes

      // Afficher les jours et heures restantes si nécessaire
      return `${days} jour${days > 1 ? 's' : ''}${remainingHours > 0 ? ` et ${remainingHours}h` : ''}`;
    } else {
      // Format standard en heures:minutes pour les durées plus courtes
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours}h${mins > 0 ? ` ${mins}m` : ''}`;
    }
  };

  // Calculer les statistiques globales
  const globalStats = useMemo(() => {
    const totalStores = storesData.length;
    const totalVisits = storesData.reduce((sum, store) => sum + store.totalVisits, 0);
    const completedVisits = storesData.reduce((sum, store) => sum + store.completedVisits, 0);
    const totalDuration = storesData.reduce((sum, store) => sum + store.totalDuration, 0);
    // Same denominator as the per-store average: measured visits, not all of them.
    const measuredVisits = storesData.reduce((sum, store) => sum + (store.measuredVisits || 0), 0);
    const avgDuration = measuredVisits > 0 ? Math.round(totalDuration / measuredVisits) : 0;
    // Rounded: the shares are fractional and formatDuration does `minutes % 60`,
    // which prints "22h 25.700000000000045m" on an unrounded total.
    const measuredDuration = Math.round(totalDuration);

    // Calculer le nombre de magasins effectivement visités
    const visitedStores = storesData.filter(store => store.completedVisits > 0).length;

    // Calculer le pool de magasins restant à visiter
    const remainingStores = totalStores - visitedStores;

    // Calculer le pourcentage de couverture
    const coverageRate = totalStores > 0 ? Math.round((visitedStores / totalStores) * 100) : 0;

    // Magasins les plus et moins visités - en utilisant completedVisits au lieu de totalVisits
    const mostVisited = storesData.length > 0 ?
      [...storesData].sort((a, b) => b.completedVisits - a.completedVisits)[0] : null;
    const leastVisited = storesData.length > 0 ?
      [...storesData].filter(s => s.completedVisits > 0).sort((a, b) => a.completedVisits - b.completedVisits)[0] : null;

    return {
      totalStores,
      visitedStores,
      remainingStores,
      coverageRate,
      totalVisits,
      completedVisits,
      avgDuration,
      // The measured total. Deriving it as completedVisits × avgDuration doubled
      // it on a two-univers project: that count is univers-ROWS while avgDuration
      // is per measured visit.
      measuredDuration,
      mostVisited,
      leastVisited,
    };
  }, [storesData]);

  return (
    <div className="space-y-6">
      {/* Statistiques globales */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300">Points de vente (total)</h4>
          <p className="text-2xl font-bold text-blue-800 dark:text-blue-200">{globalStats.totalStores}</p>
        </div>

        <div className="bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-emerald-700 dark:text-emerald-300">Points visités</h4>
          <p className="text-2xl font-bold text-emerald-800 dark:text-emerald-200">
            {globalStats.visitedStores}
            <span className="text-sm font-normal ml-2">
              ({globalStats.coverageRate}%)
            </span>
          </p>
        </div>

        <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-amber-700 dark:text-amber-300">Points restants</h4>
          <p className="text-2xl font-bold text-amber-800 dark:text-amber-200">
            {globalStats.remainingStores}
          </p>
        </div>

        <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-purple-700 dark:text-purple-300">Durée moyenne</h4>
          <p className="text-2xl font-bold text-purple-800 dark:text-purple-200">
            {formatDuration(globalStats.avgDuration)}
          </p>
        </div>
      </div>

      {/* Ligne additionnelle pour les statistiques de visites */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-green-700 dark:text-green-300">Visites réalisées</h4>
          <p className="text-2xl font-bold text-green-800 dark:text-green-200">
            {globalStats.completedVisits} / {globalStats.totalVisits}
            <span className="text-sm font-normal ml-2">
              ({globalStats.totalVisits > 0 ? Math.round((globalStats.completedVisits / globalStats.totalVisits) * 100) : 0}%)
            </span>
          </p>
        </div>

        <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg">
          <h4 className="text-sm font-medium text-indigo-700 dark:text-indigo-300">Temps total en visite</h4>
          <p className="text-2xl font-bold text-indigo-800 dark:text-indigo-200">
            {formatDuration(globalStats.measuredDuration)}
          </p>
        </div>
      </div>

      {globalStats.mostVisited && globalStats.leastVisited && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Point de vente le plus visité</h4>
            <p className="text-lg font-semibold text-gray-800 dark:text-gray-200">
              {globalStats.mostVisited.storeId} ({globalStats.mostVisited.shopSign})
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {globalStats.mostVisited.completedVisits} visites - {formatDuration(globalStats.mostVisited.averageDuration)} en moyenne
            </p>
          </div>

          <div className="bg-white dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Point de vente le moins visité</h4>
            <p className="text-lg font-semibold text-gray-800 dark:text-gray-200">
              {globalStats.leastVisited.storeId} ({globalStats.leastVisited.shopSign})
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {globalStats.leastVisited.completedVisits} visites - {formatDuration(globalStats.leastVisited.averageDuration)} en moyenne
            </p>
          </div>
        </div>
      )}

      {/* Filtres et options pour le graphique */}
      <div className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
        <div className="flex-grow">
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Rechercher par enseigne ou point de vente
          </label>
          <input
            type="text"
            className="p-2 border rounded w-full dark:bg-gray-800 dark:border-gray-600 dark:text-white"
            value={filterShopSign}
            onChange={(e) => setFilterShopSign(e.target.value)}
            placeholder="Nom de l'enseigne ou identifiant..."
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Trier par
          </label>
          <select
            className="p-2 border rounded w-full dark:bg-gray-800 dark:border-gray-600 dark:text-white"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="visits">Nombre de visites</option>
            <option value="annualVisits">Passages / an</option>
            <option value="duration">Durée moyenne</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
            Nombre à afficher
          </label>
          <select
            className="p-2 border rounded w-full dark:bg-gray-800 dark:border-gray-600 dark:text-white"
            value={showCount}
            onChange={(e) => setShowCount(parseInt(e.target.value, 10))}
          >
            <option value="5">Top 5</option>
            <option value="10">Top 10</option>
            <option value="20">Top 20</option>
            <option value="50">Top 50</option>
          </select>
        </div>
      </div>

      {/* Graphique */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="h-80">
          <Bar data={chartData} options={chartOptions} />
        </div>
      </div>

      {/* Tableau des points de vente avec SortableTable */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-white">Détail des points de vente</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Période: {dateRange}
          </p>
        </div>

        <SortableTable
          columns={columns}
          data={storesData}
          emptyMessage="Aucun point de vente trouvé pour la période sélectionnée."
          countLabel={`Total: ${storesData.length} points de vente`}
        />
      </div>

      {/* Graphique de couverture des points de vente */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mt-4">
        <h3 className="text-md font-medium text-gray-800 dark:text-white mb-3">
          Couverture des points de vente ({globalStats.coverageRate}%)
        </h3>

        <div className="relative h-6 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className="absolute top-0 left-0 h-full bg-emerald-500 dark:bg-emerald-600 rounded-full"
            style={{ width: `${globalStats.coverageRate}%` }}
          ></div>

          <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center text-xs font-medium">
            {globalStats.visitedStores} / {globalStats.totalStores} points de vente
          </div>
        </div>

        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
          <div>Visités: {globalStats.visitedStores}</div>
          <div>Restants: {globalStats.remainingStores}</div>
        </div>
      </div>
    </div>
  );
}

export default VisitsStoresAnalysis;